/**
 * Landing v3 — the "100% generated from your sources" tile's picture (Figma
 * 118:3142, user 2026-09-29): a page of the reader's own document, a passage
 * on it highlighted, and from that passage a line to each thing Flashka makes
 * of it — flashcards, a quiz, a mind map, an oral exam, a case study.
 *
 * The page is drawn, not a screenshot of someone's website: a short text on
 * brain anatomy (ours) and the landing's public-domain skull, laid out at
 * two and a half times its size and scaled down, so it reads as a document
 * thumbnail. When the tile arrives the passage is highlighted, the lines draw
 * out of it and the chips land at their ends; then the chips bob gently while
 * the tile is near the screen. Still under reduced motion.
 */
import { motion, useInView, useReducedMotion } from 'framer-motion';
import { FileTextIcon } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

import { LANDING_FEATURES } from './landing-features';
import { useFitWidth } from './use-fit-width';

/** The picture's own box (the tray's inside, Figma 118:3146). */
const BOX = { w: 461, h: 252 } as const;
/** The page (Figma 118:3148): 141 wide at 171 : 241, 19px down, 44.5 in. */
const PAGE = { x: 44.5, y: 19, w: 141, h: 199 } as const;
/** The page is laid out at this multiple and scaled down to its box. */
const PAGE_ZOOM = 2.5;
/** Where the lines leave the page: its right edge, at the passage. */
const FROM = { x: PAGE.x + PAGE.w - 1, y: 107 } as const;

/** The chips at the lines' ends (Figma 123:8637 …): centre and tilt. */
const TARGETS = [
  { id: 'flashcards', x: 374, y: 54, rotate: -9.21 },
  { id: 'quizzes', x: 302, y: 86, rotate: -14.55 },
  { id: 'mindMaps', x: 241, y: 132, rotate: -9.93 },
  { id: 'caseStudies', x: 388, y: 142, rotate: 24.77 },
  { id: 'oralExam', x: 313, y: 172, rotate: 12.55 },
] as const;

/** A line from the passage to a chip: level as it leaves the page, level as
 *  it arrives, curving between. */
function linkPath(to: { x: number; y: number }) {
  const dx = to.x - FROM.x;
  return `M ${FROM.x} ${FROM.y} C ${FROM.x + dx * 0.45} ${FROM.y} ${to.x - dx * 0.5} ${to.y} ${to.x} ${to.y}`;
}

const pct = (v: number, of: number) => `${(v / of) * 100}%`;

/** The document's page, at `PAGE_ZOOM` times its drawn size: the reader's
 *  notes on brain anatomy, in the page's language. */
function DocumentPage({ play }: { play: boolean }) {
  const { t } = useTranslation();
  return (
    <div
      className="absolute left-0 top-0 origin-top-left overflow-clip bg-secondary-50 px-5 py-4 font-serif text-secondary-800"
      style={{
        width: PAGE.w * PAGE_ZOOM,
        height: PAGE.h * PAGE_ZOOM,
        transform: `scale(${1 / PAGE_ZOOM})`,
      }}>
      {/* Indented past the file badge in the page's corner. */}
      <p className="pl-8 font-sans text-[9px] text-secondary-400">
        {t('landingV3.tiles.sources.page.kicker')}
      </p>
      <p className="mt-2 text-[21px] font-bold leading-[25px]">
        {t('landingV3.tiles.sources.page.title')}
      </p>
      <div className="mt-2 h-px bg-secondary-200" />
      <p className="mt-3 text-[13px] font-bold">{t('landingV3.tiles.sources.page.q1')}</p>
      <p className="mt-1 text-[10px] leading-[14px] text-secondary-600">
        {t('landingV3.tiles.sources.page.a1')}
      </p>
      <p className="mt-3 text-[13px] font-bold">{t('landingV3.tiles.sources.page.q2')}</p>
      <p className="mt-1 text-[10px] leading-[14px] text-secondary-600">
        {/* The passage everything is made from (Figma 123:8654 …). */}
        <mark
          className={cn(
            'rounded-[2px] bg-transparent text-secondary-800 transition-colors duration-700 [box-decoration-break:clone]',
            play && 'bg-primary-400/30',
          )}>
          {t('landingV3.tiles.sources.page.highlight')}
        </mark>{' '}
        {t('landingV3.tiles.sources.page.a2')}
      </p>
      <img
        src="/assets/landing/occlusion/skull-bones-blank.svg"
        alt=""
        width={740}
        height={576}
        loading="lazy"
        decoding="async"
        className="mx-auto mt-3 w-[70%]"
      />
      <p className="mt-2 text-[10px] leading-[14px] text-secondary-600">
        {t('landingV3.tiles.sources.page.a3')}
      </p>
    </div>
  );
}

export function SourcesDiagram({ play }: { play: boolean }) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion() ?? false;
  const shown = play || reduceMotion;
  const ref = useRef<HTMLDivElement>(null);
  const near = useInView(ref, { margin: '200px 0px' });
  // The tray's padding (4px a side) is not the picture's.
  const scale = useFitWidth(ref, BOX.w, 8);
  const chips = TARGETS.map((target) => ({
    ...target,
    feature: LANDING_FEATURES.find((f) => f.id === target.id),
  }));
  return (
    // The tray (Figma 118:3142): the tile's inner well, 260 tall on the
    // frame. The picture is drawn at its own size and scaled whole to a
    // narrower tray (a phone's).
    <div
      ref={ref}
      className="flex w-full justify-center overflow-clip rounded-[28px] bg-secondary-100/70 p-1">
      <div
        aria-hidden
        className="relative shrink-0"
        style={{ width: BOX.w * scale, height: BOX.h * scale }}>
        <div
          className="absolute left-0 top-0 origin-top-left"
          style={{
            width: BOX.w,
            height: BOX.h,
            transform: scale < 1 ? `scale(${scale})` : undefined,
          }}>
          {/* The source (Figma 118:3147): the page, its name under it. */}
          <div
            className="absolute flex flex-col items-center gap-[3px] drop-shadow-[0px_1px_2px_rgba(0,0,0,0.1)]"
            style={{
              left: pct(PAGE.x, BOX.w),
              top: pct(PAGE.y, BOX.h),
              width: pct(PAGE.w, BOX.w),
            }}>
            <div
              className="relative w-full overflow-clip rounded-[12px] border-[0.5px] border-secondary-200"
              style={{ aspectRatio: PAGE.w / PAGE.h }}>
              <DocumentPage play={shown} />
              <span className="absolute left-[5px] top-[5px] flex h-[15px] w-[15px] items-center justify-center rounded-full border-[0.5px] border-secondary-100 bg-secondary-50/70">
                <FileTextIcon className="h-[7px] w-[7px] text-fg-subtle" />
              </span>
            </div>
            <span className="w-full truncate text-center text-[7.5px] font-semibold leading-[11px] text-secondary-600">
              {t('landingV3.tiles.sources.file')}
            </span>
          </div>
          {/* The lines, drawn out of the passage (Figma 123:8657 …). */}
          <svg
            viewBox={`0 0 ${BOX.w} ${BOX.h}`}
            className="absolute inset-0 h-full w-full overflow-visible"
            fill="none">
            {chips.map((chip, i) => (
              <motion.path
                key={chip.id}
                d={linkPath(chip)}
                className="stroke-primary-300"
                strokeWidth={2}
                strokeLinecap="round"
                initial={reduceMotion ? false : { pathLength: 0, opacity: 0 }}
                animate={shown ? { pathLength: 1, opacity: 1 } : undefined}
                transition={{ duration: 0.7, delay: 0.55 + i * 0.08, ease: 'easeOut' }}
              />
            ))}
          </svg>
          {/* What the passage becomes: the features' own chips. */}
          {chips.map((chip, i) => {
            const feature = chip.feature;
            if (!feature) return null;
            const Icon = feature.icon;
            return (
              <motion.span
                key={chip.id}
                className="absolute"
                // Centred with framer's `x` / `y`: its inline transform (the
                // pop's scale) would override a Tailwind translate class.
                style={{
                  left: pct(chip.x, BOX.w),
                  top: pct(chip.y, BOX.h),
                  x: '-50%',
                  y: '-50%',
                }}
                initial={reduceMotion ? false : { opacity: 0, scale: 0.6 }}
                animate={shown ? { opacity: 1, scale: 1 } : undefined}
                transition={{
                  delay: 0.95 + i * 0.08,
                  type: 'spring',
                  stiffness: 320,
                  damping: 20,
                }}>
                <span
                  className={cn(
                    'lv3-chip-float flex items-center justify-center rounded-[14px] border p-2.5',
                    feature.chipClass,
                    feature.shadowClass,
                  )}
                  style={
                    {
                      '--lv3-tilt': `${chip.rotate}deg`,
                      animationDuration: `${3.4 + i * 0.45}s`,
                      animationDelay: `-${i * 0.7}s`,
                      animationPlayState: near ? 'running' : 'paused',
                    } as React.CSSProperties
                  }>
                  <Icon className={cn('h-6 w-6', feature.iconClass)} />
                </span>
              </motion.span>
            );
          })}
        </div>
      </div>
    </div>
  );
}
